import { customerFreemiumAllowed } from 'src/data/accessRights';
import { alignChannelWithBackend } from 'src/data/channel';
import type { FeatureLimit } from 'src/data/featureLimits';
import {
  // @ts-expect-error Missing types
  getCustomerPlan,
  isPlanFromBasicCategory,
  SUBSCRIPTION_STATUS,
} from 'src/data/subscription';
import type { ContentTaxonomy } from 'src/data/types';
import type { Entity } from 'src/lib/collection';

export interface Customer extends Entity {
  name: string;
  lifecycle?: CustomerLifecycle;
  maxMembers?: number;
  segment?: CustomerSegment;
  isKeywee?: boolean;
  usesSso?: boolean;
  featureFlags?: string[];
  featureAccess?: string[];
  featureLimits?: FeatureLimit[];
  inRetrial?: boolean;
  contentTaxonomy?: ContentTaxonomy;
  billingConfiguration?: BillingConfiguration;
  rowsAllocationPerChannel?: RowsAllocation[];
  planMigrationStatus?: {
    acceptStatus?: string;
  };
}

interface QuotaInfo {
  isExceedLimit: boolean;
  limit: number;
}

interface BillingConfiguration {
  queryQuota?: {
    planWordsLimit?: number;
    planWordsUsed?: number;
    overageWordsLimit?: number;
    overageWordsUsed?: number;
  };
  method?: {
    subscriptionStatus?: string;
  };
}

interface RowsAllocation {
  channel: string;
  rowsAllocated: number;
  rowsCount: number;
}

/**
 * Customer lifecycle
 */
export const CUSTOMER_LIFECYCLE = {
  active: 'active',
  inTrial: 'in_trial',
  trialEnded: 'trial_ended',
  notPaying: 'not_paying',
  paused: 'paused',
  pocEnded: 'poc_ended',
  inMobileConfirmed: 'in_mobile_confirmed',
} as const;
export type CustomerLifecycle = (typeof CUSTOMER_LIFECYCLE)[keyof typeof CUSTOMER_LIFECYCLE];

const LIFECYCLE_LABEL = {
  [CUSTOMER_LIFECYCLE.active]: 'Active',
  [CUSTOMER_LIFECYCLE.inTrial]: 'In trial',
  [CUSTOMER_LIFECYCLE.trialEnded]: 'Trial ended',
  [CUSTOMER_LIFECYCLE.paused]: 'Paused',
  [CUSTOMER_LIFECYCLE.pocEnded]: 'POC ended',
  [CUSTOMER_LIFECYCLE.notPaying]: 'Subscription ended',
  [CUSTOMER_LIFECYCLE.inMobileConfirmed]: 'Confirmed',
};

export const isLifecycleInTrial = (lifecycle?: CustomerLifecycle): boolean =>
  lifecycle === CUSTOMER_LIFECYCLE.inTrial;

export function getCustomerLifecycleLabel(lifecycle: CustomerLifecycle): string {
  return LIFECYCLE_LABEL[lifecycle] || lifecycle;
}

export function customerHasActiveSubscription(customer: Customer): boolean {
  return customer?.lifecycle === CUSTOMER_LIFECYCLE.active;
}

export function customerInTrial(customer: Customer): boolean {
  return isLifecycleInTrial(customer?.lifecycle);
}

export function customerTrialEnded(customer: Customer): boolean {
  return (
    customer?.lifecycle === CUSTOMER_LIFECYCLE.trialEnded ||
    customer?.lifecycle === CUSTOMER_LIFECYCLE.pocEnded
  );
}

export function customerInTrialOrTrialEnded(customer: Customer): boolean {
  return customerInTrial(customer) || customerTrialEnded(customer);
}

export function customerSubscriptionExpired(customer: Customer): boolean {
  return customer?.lifecycle === CUSTOMER_LIFECYCLE.notPaying;
}

export function customerSubscriptionPaused(customer: Customer): boolean {
  return customer?.lifecycle === CUSTOMER_LIFECYCLE.paused;
}

export function isCustomerQuotaEnded(customer: Customer): boolean {
  const quota = customer?.billingConfiguration?.queryQuota;

  // If customer don't have limit set, then treat quota as infinite
  if (!quota?.planWordsLimit) {
    return false;
  }

  // Check 'plan' and 'overage' quotas at once
  const used = (quota.planWordsUsed || 0) + (quota.overageWordsUsed || 0);
  const limit = (quota.planWordsLimit || 0) + (quota.overageWordsLimit || 0);

  return used >= limit;
}

export function getCustomerQuotaUsed(customer: Customer): number {
  const quota = customer?.billingConfiguration?.queryQuota;

  // If customer doesn't have limit set, then it is though he has used zero credits.
  if (!quota?.planWordsLimit) {
    return 0;
  }

  return (quota.planWordsUsed || 0) + (quota.overageWordsUsed || 0);
}

export function getCustomerQuotaLeft(customer: Customer): number {
  const quota = customer?.billingConfiguration?.queryQuota;

  // If customer doesn't have limit set, then treat quota as infinite
  if (!quota?.planWordsLimit) {
    return Infinity;
  }

  const used = (quota.planWordsUsed || 0) + (quota.overageWordsUsed || 0);
  const limit = (quota.planWordsLimit || 0) + (quota.overageWordsLimit || 0);

  return limit - used;
}

/**
 * Customer segments
 */
export const CUSTOMER_SEGMENT = {
  marketer: 'Marketer',
  publisher: 'Publisher',
  other: 'Other',
} as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENT)[keyof typeof CUSTOMER_SEGMENT];

export const customerHasEmptyMigrationStatus = (customer: Customer): boolean =>
  customer?.planMigrationStatus?.acceptStatus === null;

/**
 * Other
 */

export function isCustomerFeatureEnabled(customer: Customer, featureFlag: string): boolean {
  if (!customer) return false;

  const customerFeatures = customer.featureFlags || [];
  return customerFeatures.includes(featureFlag);
}

export const isCustomerOnFreemium = (customer: Customer): boolean => {
  const plan = getCustomerPlan(customer);

  return isPlanFromBasicCategory(plan) && customerFreemiumAllowed(customer);
};

export const isCustomerSubscriptionCanceled = (customer: Customer): boolean => {
  const { subscriptionStatus } = customer?.billingConfiguration?.method || {};

  return subscriptionStatus === SUBSCRIPTION_STATUS.canceled;
};

/**
 * Check if customer reached rows allocation limit for a channel
 * @param customer - customer object
 * @param channel - channel
 * @return - isExceedLimit - if customer reached rows allocation limit for a channel, limit - the number of used rows up to the limit
 */
export const getCustomerRowsAllocationLimitInfo = (
  customer: Customer,
  channel: string
): QuotaInfo | undefined => {
  const alignedChannel = alignChannelWithBackend(channel);
  const allocation = customer?.rowsAllocationPerChannel?.find(
    (item) => item.channel === alignedChannel
  );
  if (!allocation) {
    return;
  }

  return {
    isExceedLimit: allocation.rowsAllocated < allocation.rowsCount,
    limit: allocation.rowsAllocated,
  };
};

export const CUSTOMER_IDS_WITHOUT_IMAGE_GENERATION_FEATURE = [1405765];
